import { hasAdminRights } from '@gonfalon/members/hasAdminRights';
import { hasStrictWriterRights } from '@gonfalon/members/hasStrictWriterRights';
import { isReader } from '@gonfalon/members/isReader';
import { type Member } from '@gonfalon/members/types';
import type { ResourceSpecifier } from '@gonfalon/resource-specifiers/ast';

import type { ResourceActions } from '../actions';
import type { Access, AccessCheck } from '../internal/types';

import { hasDeniedActions } from './hasDeniedActions';

const defaultDenyCheck = isReader;
const defaultAllowCheck = hasStrictWriterRights;

type MemberPermissionCheck = (member: Member) => boolean;

export function checkAccess(
  member: Member,
  access: Access | undefined,
  action: string,
  options?: { builtInAllowCheck?: MemberPermissionCheck; builtInDenyCheck?: MemberPermissionCheck },
): AccessCheck {
  const builtInDenyCheck = options?.builtInDenyCheck ?? defaultDenyCheck;
  const builtInAllowCheck = options?.builtInAllowCheck ?? defaultAllowCheck;
  if (builtInDenyCheck(member)) {
    return { isAllowed: false, appliedRoleName: 'reader' };
  }

  if (builtInAllowCheck(member) || !hasDeniedActions(access)) {
    return { isAllowed: true };
  }

  const denied = access?.denied ?? [];
  const deniedAction = denied.find((v) => v.action === action);

  if (deniedAction === undefined) {
    return { isAllowed: true };
  }

  const reason = deniedAction.reason;
  const roleName = reason?.role_name;

  return {
    isAllowed: false,
    appliedRoleName: roleName,
    reason,
  };
}

/**
 * The below code will replace everything above, and will replace all
 * specific variants of checkAccess, such as checkAccountAccess, with a single function
 */
function getDenyCheck<T extends ResourceSpecifier['type']>(resourceType: T) {
  switch (resourceType) {
    default:
      return defaultDenyCheck;
  }
}

function getAllowCheck<T extends ResourceSpecifier['type']>(resourceType: T) {
  switch (resourceType) {
    case 'acct':
      return hasAdminRights;
    default:
      return defaultAllowCheck;
  }
}

// todo - rename once all instances of existing checkAccess are updated
export function genericCheckAccess<T extends ResourceSpecifier['type']>(
  resourceType: T,
  member: Member,
  action: ResourceActions<T> | Array<ResourceActions<T>>,
  access?: Access,
): AccessCheck {
  const denyCheck = getDenyCheck(resourceType);
  const allowCheck = getAllowCheck(resourceType);

  if (denyCheck(member)) {
    return { isAllowed: false, appliedRoleName: 'reader' };
  }

  if (allowCheck(member) || !hasDeniedActions(access)) {
    return { isAllowed: true };
  }

  const denied = access?.denied ?? [];

  /**
   * Allows consumers to provide an array of actions to check access for, and will return true if at least one of the actions is not denied.
   * Example legacy function if access checks are used independently:
   * function canManageReleasePipelines(rp: ReleasePipeline, member: Member) {
        const canCreateReleasePipelines = genericCheckAccess('release-pipeline', member, 'createReleasePipeline');
        const canUpdateReleasePipelines = genericCheckAccess('release-pipeline', member, 'updateReleasePipeline', rp._access);
        const canDeleteReleasePipelines = genericCheckAccess('release-pipeline', member, 'deleteReleasePipeline', rp._access);
        return (
          canCreateReleasePipelines.isAllowed || canUpdateReleasePipelines.isAllowed || canDeleteReleasePipelines.isAllowed
        );
      }
   */
  if (Array.isArray(action)) {
    // OR semantics: allow if at least one provided action is NOT denied
    const deniedSet = new Set<ResourceActions<T>>(denied.map((v) => v.action as ResourceActions<T>));
    const anyAllowed = action.some((a) => !deniedSet.has(a));
    if (anyAllowed) {
      return { isAllowed: true };
    }

    // All provided actions are denied; surface the first matching denial's reason
    const firstDenied = denied.find((v) => action.includes(v.action as ResourceActions<T>));
    const reason = firstDenied?.reason;
    const roleName = reason?.role_name;

    return {
      isAllowed: false,
      appliedRoleName: roleName,
      reason,
    };
  }

  const deniedAction = denied.find((v) => (v.action as ResourceActions<T>) === action);

  if (deniedAction === undefined) {
    return { isAllowed: true };
  }

  const reason = deniedAction.reason;
  const roleName = reason?.role_name;

  return {
    isAllowed: false,
    appliedRoleName: roleName,
    reason,
  };
}
